/**
 * ReticleRenderer - Renders ground targeting indicators and overhead pointers
 * Red for hostile monsters, Cyan for friendly allies.
 */

import { EntityState } from '../../core/Types';
import { tileToScreen, TILE_WIDTH, TILE_HEIGHT } from './IsoMath';

export class ReticleRenderer {
  public renderGroundReticle(
    ctx: CanvasRenderingContext2D,
    target: EntityState,
    camX: number,
    camY: number,
    time: number
  ): void {
    if (target.isDead) return;

    const screenPos = tileToScreen(target.x, target.y, camX, camY);
    const isFriendly = target.category === 'companion' || target.category === 'player';
    const mainColor = isFriendly ? '#38bdf8' : '#ef4444';
    const glowColor = isFriendly ? 'rgba(56, 189, 248, 0.4)' : 'rgba(239, 68, 68, 0.4)';

    ctx.save();
    ctx.translate(screenPos.x, screenPos.y);

    // Pulse size
    const pulse = Math.sin(time * 0.005) * 2;
    const halfW = TILE_WIDTH / 2 + pulse;
    const halfH = TILE_HEIGHT / 2 + pulse * 0.5;

    // Glowing diamond fill
    ctx.beginPath();
    ctx.moveTo(0, -halfH);
    ctx.lineTo(halfW, 0);
    ctx.lineTo(0, halfH);
    ctx.lineTo(-halfW, 0);
    ctx.closePath();

    ctx.fillStyle = glowColor;
    ctx.fill();

    ctx.lineWidth = 2.5;
    ctx.strokeStyle = mainColor;
    ctx.shadowColor = mainColor;
    ctx.shadowBlur = 8;
    ctx.stroke();

    // Four animated brackets
    const bracketLen = 6;
    ctx.lineWidth = 3;
    // Top
    ctx.beginPath();
    ctx.moveTo(-bracketLen, -halfH);
    ctx.lineTo(bracketLen, -halfH);
    ctx.stroke();
    // Bottom
    ctx.beginPath();
    ctx.moveTo(-bracketLen, halfH);
    ctx.lineTo(bracketLen, halfH);
    ctx.stroke();

    ctx.restore();
  }

  public renderOverheadPointer(
    ctx: CanvasRenderingContext2D,
    target: EntityState,
    camX: number,
    camY: number,
    time: number
  ): void {
    if (target.isDead) return;

    const screenPos = tileToScreen(target.x, target.y, camX, camY);
    const isFriendly = target.category === 'companion' || target.category === 'player';
    const color = isFriendly ? '#38bdf8' : '#ef4444';

    const bob = Math.sin(time * 0.006) * 4;
    const topY = screenPos.y - 48 + bob;

    ctx.save();
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;

    // Inverted Triangle
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 7, topY - 10);
    ctx.lineTo(screenPos.x + 7, topY - 10);
    ctx.lineTo(screenPos.x, topY);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }
}
